package com.shadowings.gero.presentation.screen

import com.shadowings.gero.presentation.util.DevicePreviews
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.PrimaryTabRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.avatars.MaskoAvatar
import com.shadowings.gero.presentation.component.avatars.ZetaAvatar
import com.shadowings.gero.presentation.screen.characters.AlynCharacterScreen
import com.shadowings.gero.presentation.screen.characters.MaskoCharacterScreen
import com.shadowings.gero.presentation.screen.characters.NiloCharacterScreen
import com.shadowings.gero.presentation.screen.characters.ZetaCharacterScreen
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.viewmodel.ProfileViewModel
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.mib_mistery
import gero.composeapp.generated.resources.character_locked_hint
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
fun CharacterScreenPreview() {
    AppTheme {
        Surface {
            CharacterScreenContent(
                modifier = Modifier,
                userData = UserData(alynColors = mapOf("skin" to 2, "earrings" to 4)),
                onAlynColorChanged = { _, _ -> }
            )
        }
    }
}

@Composable
fun CharacterScreen(
    modifier: Modifier = Modifier,
    onPurchaseFullVersion: () -> Unit = {},
    viewModel: ProfileViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    CharacterScreenContent(
        modifier = modifier,
        userData = uiState.userData,
        onAlynColorChanged = { categoryKey, index -> viewModel.setAlynColor(categoryKey, index) },
        onNiloColorChanged = { categoryKey, index -> viewModel.setNiloColor(categoryKey, index) },
        onZetaColorChanged = { categoryKey, index -> viewModel.setZetaColor(categoryKey, index) },
        onMaskoColorChanged = { categoryKey, index -> viewModel.setMaskoColor(categoryKey, index) },
        onResetAlyn = { viewModel.resetAlynColors() },
        onResetNilo = { viewModel.resetNiloColors() },
        onResetZeta = { viewModel.resetZetaColors() },
        onResetMasko = { viewModel.resetMaskoColors() },
        onPurchaseFullVersion = onPurchaseFullVersion,
        onTabSelected = { viewModel.onCharacterTabClick(it) }
    )
}

@Composable
fun CharacterScreenContent(
    modifier: Modifier,
    userData: UserData,
    onAlynColorChanged: (categoryKey: String, index: Int) -> Unit = { _, _ -> },
    onNiloColorChanged: (categoryKey: String, index: Int) -> Unit = { _, _ -> },
    onZetaColorChanged: (categoryKey: String, index: Int) -> Unit = { _, _ -> },
    onMaskoColorChanged: (categoryKey: String, index: Int) -> Unit = { _, _ -> },
    onResetAlyn: () -> Unit = {},
    onResetNilo: () -> Unit = {},
    onResetZeta: () -> Unit = {},
    onResetMasko: () -> Unit = {},
    onPurchaseFullVersion: () -> Unit = {},
    onTabSelected: (Int) -> Unit = {}
) {
    val hapticFeedback = LocalHapticFeedback.current
    val isZetaUnlocked = userData.explanations.contains(LessonIds.END_VERB_1)
    val isMaskoUnlocked = userData.explanations.contains(LessonIds.ENDING_VSO)
    val characters = listOf("Alyn", "Nilo", if (isZetaUnlocked) "Zeta" else "???", if (isMaskoUnlocked) "Masko" else "???")
    var selectedDestination by rememberSaveable { mutableIntStateOf(0) }

    Column(modifier = modifier) {
        PrimaryTabRow(
            selectedTabIndex = selectedDestination,
            modifier = Modifier.fillMaxWidth().semantics { testTag = "characters_tab_row" }
        ) {
            characters.forEachIndexed { index, destination ->
                Tab(
                    selected = selectedDestination == index,
                    onClick = {
                        hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                        selectedDestination = index
                        onTabSelected(index)
                    },
                    modifier = Modifier.semantics {
                        testTag = "characters_tab_${destination.lowercase().replace("?", "locked")}"
                    },
                    text = {
                        Text(
                            text = destination,
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                )
            }
        }
        when (selectedDestination) {
            0 -> AlynCharacterScreen(
                userData = userData,
                onAlynColorChanged = onAlynColorChanged,
                onResetClick = onResetAlyn,
                onPurchaseFullVersion = onPurchaseFullVersion
            )

            1 -> NiloCharacterScreen(
                userData = userData,
                onNiloColorChanged = onNiloColorChanged,
                onResetClick = onResetNilo,
                onPurchaseFullVersion = onPurchaseFullVersion
            )

            2 -> {
                if (isZetaUnlocked) {
                    ZetaCharacterScreen(
                        userData = userData,
                        onZetaColorChanged = onZetaColorChanged,
                        onResetClick = onResetZeta,
                        onPurchaseFullVersion = onPurchaseFullVersion
                    )
                } else {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        ZetaAvatar(
                            userData = userData,
                            face = Faces.AllBlack,
                            modifier = Modifier.height(64.dp),
                            withBackground = false
                        )
                        Spacer(Modifier.size(24.dp))
                        Text(
                            text = stringResource(Res.string.character_locked_hint),
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.padding(horizontal = 32.dp)
                        )
                    }
                }
            }

            3 -> {
                if (isMaskoUnlocked) {
                    MaskoCharacterScreen(
                        userData = userData,
                        onMaskoColorChanged = onMaskoColorChanged,
                        onResetClick = onResetMasko,
                        onPurchaseFullVersion = onPurchaseFullVersion
                    )
                } else {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        MaskoAvatar(
                            userData = userData,
                            face = Faces.AllBlack,
                            modifier = Modifier.height(64.dp),
                            withBackground = false
                        )
                        Spacer(Modifier.size(24.dp))
                        Text(
                            text = stringResource(Res.string.character_locked_hint),
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.padding(horizontal = 32.dp)
                        )
                    }
                }
            }
        }
    }
}
